import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ImpersonationTokenHolder } from 'app/core/auth/impersonation-token.holder';
import { ImpersonationService } from 'app/core/auth/impersonation.service';
import { handleSessionExpired } from 'app/core/auth/session-expired';
import { KeycloakService } from 'keycloak-angular';
import { EMPTY, Observable, catchError, from, of, switchMap } from 'rxjs';

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const keycloackService = inject(KeycloakService);
    const impersonationService = inject(ImpersonationService);

    if (req.url.includes('evolus')) {
        return next(req.clone());
    }

    if (keycloackService.isLoggedIn()) {
        return of(true).pipe(
            switchMap(() => {
                const impersonatedToken = ImpersonationTokenHolder.token;
                // If the impersonated token has expired, stop impersonation and fall back to the real token
                if (impersonatedToken && !impersonationService.stopIfTokenExpired()) {
                    return of(impersonatedToken);
                }
                if (keycloackService.isTokenExpired(60)) {
                    return from(keycloackService.updateToken()).pipe(
                        switchMap(() => keycloackService.getToken()),
                        catchError(() => {
                            // Refresh token also expired — back to the login page instead of leaving user stuck on 401s
                            handleSessionExpired(keycloackService);
                            return EMPTY;
                        })
                    );
                }
                return keycloackService.getToken();
            }),
            switchMap((token) => {
                const authedReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
                return next(authedReq);
            })
        );
    }

    return next(req.clone());
};
